// Effect helpers shared by LeftSection and the element panels (text, image, address, shape, logo).
// Moved verbatim from LeftSection.vue. Do NOT rename.
// The host must provide (panelContext): contentList, activeObject, selectedObjectsList, getSelectedObjectsList,
// activeObjectForLeftPanel, isMultiPropertySelection.
// The shape-shadow methods also write objectShadowAngle / objectShadowDistance / objectShadowBlur /
// tempShapeShadowColor / tempImageShadowColor, so a host that uses them also needs the imageEffects mixin.
// LeftSection only uses the text shadow / outline colour handlers (EventBus, R5) and emitUndoRedoRequest.
import _ from 'lodash';
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import {
  findObjectById,
  findParentGroup,
  resolveTopLevelSelection,
} from '@/components/Canvas/utils/groupHelpers';

export default {
  data() {
    return {
      isColorPickerOpen: false, // shadow / outline colour picker of the Effects box
    };
  },
  mounted() {
    window.addEventListener('keydown', this.closeColorPickerOnEscape);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.closeColorPickerOnEscape);
  },
  methods: {
    closeColorPickerOnEscape(e) {
      if (e.key === 'Escape' && this.isColorPickerOpen) {
        this.isColorPickerOpen = false;
      }
    },
    emitUndoRedoRequest: _.debounce((data) => {
      EventBus.$emit('addIntoPageWiseUndoRedoArray', data);
    }, 500),
    restoreSelectionAfterChange(fallbackObjects = null) {
      // Keep a drilled-in group child selected. Promoting back to the parent group
      // made Effects read a sibling without the effect and show "None".
      if (
          this.activeObject
          && this.activeObject.type !== 'group'
          && findParentGroup(this.contentList, this.activeObject.Id)
      ) {
        const childId = this.activeObject.Id;
        this.$nextTick(() => {
          // Skip if selection was cleared or changed (e.g. user selected the group
          // after crop/remove-bg while a prior restore was still pending).
          if (!this.activeObject || this.activeObject.Id !== childId) { return; }
          EventBus.$emit('onObjectSelectedFromRightPanel', childId);
        });
        return;
      }

      const groupId     = (this.activeObject && this.activeObject.type === 'group')
          ? this.activeObject.Id
          : null;
      const multiSelect = (fallbackObjects && fallbackObjects.length > 1)
          ? fallbackObjects
          : ((this.selectedObjectsList && this.selectedObjectsList.length > 1)
              ? this.selectedObjectsList.slice()
              : null);
      const singleId    = (this.activeObject && this.activeObject.type !== 'group')
          ? this.activeObject.Id
          : (this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.Id);

      const sources  = multiSelect
          || (groupId ? [{ Id: groupId, type: 'group' }] : null)
          || (singleId ? [{ Id: singleId }] : null);
      const topLevel = (this.contentList && sources)
          ? resolveTopLevelSelection(this.contentList, sources)
          : [];

      setTimeout(() => {
        if (topLevel.length > 1) {
          EventBus.$emit(
              ApplicationEventTypes.ObjectSelectionEvents.SELECT_MULTIPLE_OBJECTS,
              topLevel,
          );
          return;
        }
        if (topLevel.length === 1) {
          EventBus.$emit('onObjectSelectedFromRightPanel', topLevel[0].Id);
          return;
        }
        if (groupId) {
          EventBus.$emit('onObjectSelectedFromRightPanel', groupId);
          return;
        }
        if (multiSelect && multiSelect.length > 1) {
          EventBus.$emit(ApplicationEventTypes.ObjectSelectionEvents.SELECT_MULTIPLE_OBJECTS, multiSelect);
          return;
        }
        if (singleId) {
          EventBus.$emit('onObjectSelectedFromRightPanel', singleId);
        }
      });
    },
    getSelectedTextObjectsForEffects() {
      const selectedObjects = this.getSelectedObjectsList || [];
      if (selectedObjects.length > 0) {
        return selectedObjects
            .map(object => findObjectById(this.contentList, object.Id) || object)
            .filter(object => object && object.type === 'text');
      }

      const activeObject = this.activeObjectForLeftPanel;
      if (!activeObject || activeObject.type !== 'text') { return []; }
      const selectedObject = findObjectById(this.contentList, activeObject.Id);
      return selectedObject ? [selectedObject] : [activeObject];
    },
    updateTextShadowColor(value, objectId = '') {
      const targets = objectId
          ? [findObjectById(this.contentList, objectId)].filter(Boolean)
          : this.getSelectedTextObjectsForEffects();
      targets.forEach((object) => {
        if (!object || object.type !== 'text' || !object.textShadow) { return; }
        object.textShadow.shadowColor = value;
      });
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      this.emitUndoRedoRequest('updateTextShadowColor');
    },
    updateTextOutlineColor(value, objectId = '') {
      const targets = objectId
          ? [findObjectById(this.contentList, objectId)].filter(Boolean)
          : this.getSelectedTextObjectsForEffects();
      targets.forEach((object) => {
        if (!object || object.type !== 'text' || !object.outLine) { return; }
        object.outLine.strokeColor = value;
      });
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      this.emitUndoRedoRequest('updateTextOutlineColor');
    },
    openColorPicker() {
      if (!this.isColorPickerOpen) {
        this.isColorPickerOpen = true;
      } else {
        this.isColorPickerOpen = false;
      }
    },
    removeShapeEffect() {
      const list = this.getSelectedObjectsList;
      if (this.isMultiPropertySelection) {
        list.forEach((object) => {
          if (!object) { return; }
          if (object.type === 'shape'
              || object.ImageType === 'SVGImage'
              || (object.ImageType && object.ImageType.indexOf('FDICText') !== -1)
              || (object.ImageType && object.ImageType.indexOf('FDICLogo') !== -1)) {
            this.removeShapeEffectById(object.Id);
          }
        });
      } else if (this.activeObjectForLeftPanel) {
        this.removeShapeEffectById(this.activeObjectForLeftPanel.Id);
      }
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange();
      this.emitUndoRedoRequest('removeShapeEffect');
    },
    removeShapeEffectById(id) {
      const selectedObject = findObjectById(this.contentList, id);
      if ((selectedObject.type == 'shape' && selectedObject.shapeType == 'polygon' && selectedObject.effect && selectedObject.effect == 'outline')) {
        selectedObject.X         += 1;
        selectedObject.Y         += 1;
        selectedObject.svgScaleX -= 0.005;
        selectedObject.svgScaleY -= 0.005;
      }
      this.$set(selectedObject, 'effect', 'none');
      if (selectedObject.type == 'shape') {
        selectedObject.StrokeWidth = 0;
      } else {
        selectedObject.SvgProperty.StrokeWidth = 0;
        EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_STROKE_COLOR_BY_ID, selectedObject.SvgProperty.StrokeColor, selectedObject.Id);
      }
      const elem        = document.querySelector(`.canvas-main-html-container [id='${id}']`);
      elem.style.filter = 'none'; // Reset the filter to remove any shadow effect
    },
    applyShadowOnShape() {
      const list = this.getSelectedObjectsList;
      if (this.isMultiPropertySelection) {
        list.forEach((object) => {
          if (object.type === 'shape' || object.ImageType === 'SVGImage' || (object.ImageType && object.ImageType.indexOf('FDICText') !== -1) || (object.ImageType && object.ImageType.indexOf('FDICLogo') !== -1)) {
            this.applyShadowOnShapeById(object.Id);
          }
        });
      } else if (this.activeObjectForLeftPanel) {
        this.applyShadowOnShapeById(this.activeObjectForLeftPanel.Id);
      }
      this.emitUndoRedoRequest('Shape Shadow');
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange();
      this.emitUndoRedoRequest('applyShadowOnShape');
    },
    applyShadowOnShapeById(id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject) {
        return;
      } // Ensure selectedObject exists

      const initialDistance = 0.04; // Default distance for shadow effect
      const initailAngle    = 45; // Default angle for shadow effect
      const initailBlur     = 0.1; // Default blur for shadow effect
      let scaledDistance    = initialDistance * Math.max(selectedObject.Height, selectedObject.Width);
      scaledDistance        = selectedObject.type == 'image' && selectedObject.ImageType == 'WhiteboxLogo' ? scaledDistance / 10 : scaledDistance;
      const color           = '#846C63';

      const radians = (initailAngle * Math.PI) / 180;

      const shadowOffsetX = scaledDistance * Math.cos(radians);
      const shadowOffsetY = scaledDistance * Math.sin(radians);
      const shadow        = {
        color,
        X:        0,
        Y:        0,
        blur:     initailBlur,
        angle:    initailAngle,
        distance: initialDistance,
        shadowOffsetY,
        shadowOffsetX,
      };

      if (selectedObject.type == 'shape' && selectedObject.shapeType == 'polygon' && selectedObject.effect && selectedObject.effect == 'outline') {
        selectedObject.X         += 1;
        selectedObject.Y         += 1;
        selectedObject.svgScaleX -= 0.005;
        selectedObject.svgScaleY -= 0.005;
      }

      this.$set(selectedObject, 'effect', 'shadow');

      if (selectedObject.type == 'shape') {
        selectedObject.StrokeWidth = 0;
      } else if (selectedObject.SvgProperty && (selectedObject.ImageType === 'SVGImage' || (selectedObject.ImageType && (selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1)))) {
        selectedObject.SvgProperty.StrokeWidth = 0;
        EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_STROKE_COLOR_BY_ID, selectedObject.SvgProperty.StrokeColor, selectedObject.Id);
      }
      setTimeout(() => {
        const elem        = document.querySelector(`.canvas-main-html-container [id='${selectedObject.Id}']`);
        const filterValue = `drop-shadow(${shadow.shadowOffsetX}px ${shadow.shadowOffsetY}px ${shadow.blur * 40}px ${shadow.color})`;
        if (elem) {
          elem.style.filter = filterValue;
        }
        console.log('applyShadowOnShapeById ', filterValue);
        this.$set(selectedObject, 'shadow', shadow);
        EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
        this.objectShadowAngle    = shadow.angle;
        this.objectShadowDistance = shadow.distance;
        this.objectShadowBlur     = shadow.blur;
        this.tempShapeShadowColor = shadow.color;
        this.tempImageShadowColor = shadow.color;
      }, 100);
    },
    updateObjectShadowProperty(property, value) {
      if (property === 'angle') {
        this.objectShadowAngle = value;
      }
      if (property === 'distance') {
        this.objectShadowDistance = value;
      }
      if (property === 'blur') {
        this.objectShadowBlur = value;
      }
      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'shape' || object.type === 'image' || object.ImageType === 'SVGImage') {
          this.updateObjectShadowPropertyById(property, value, object.Id);
        }
      });

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      this.emitUndoRedoRequest('From Shape Shadow Effect');
    },
    updateObjectShadowPropertyById(property, value, id) {
      console.log('updateObjectShadowPropertyById', property, value, id);
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject) {
        return;
      } // Ensure selectedObject exists

      const shadow = (selectedObject && selectedObject.shadow) ? selectedObject.shadow : {
        color:         '#846C63',
        X:             0,
        Y:             0,
        blur:          0,
        angle:         0,
        distance:      this.objectShadowDistance,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
      };

      // const fontSize = parseInt(selectedObject.fontSize, 10) || 16;

      if (property === 'angle' || property === 'distance') {
        shadow.angle    = property === 'angle' ? value : shadow.angle;
        shadow.distance = property === 'distance' ? value : shadow.distance;

        const scaledDistance = shadow.distance * Math.max(selectedObject.Height, selectedObject.Width);

        const radians = (shadow.angle * Math.PI) / 180;

        shadow.shadowOffsetX = scaledDistance * Math.cos(radians);
        shadow.shadowOffsetY = scaledDistance * Math.sin(radians);
      } else {
        shadow[property] = property !== 'color' ? parseFloat(value) : value;
      }
      selectedObject.shadow = shadow;

      // apply shadow to the element;
      const elem = document.querySelector(`.canvas-main-html-container [id='${selectedObject.Id}']`);
      if (elem) {
        elem.style.filter = `drop-shadow(${shadow.shadowOffsetX}px ${shadow.shadowOffsetY}px ${shadow.blur * 40}px ${shadow.color})`;
      }
    },
    applyOutlineOnShape() {
      const list = this.getSelectedObjectsList;
      if (this.isMultiPropertySelection) {
        list.forEach((object) => {
          if (!object) { return; }
          if (object.type === 'shape'
              || object.ImageType === 'SVGImage'
              || (object.ImageType && object.ImageType.indexOf('FDICText') !== -1)
              || (object.ImageType && object.ImageType.indexOf('FDICLogo') !== -1)) {
            this.applyOutlineOnShapeById(object.Id);
          }
        });
      } else if (this.activeObjectForLeftPanel) {
        this.applyOutlineOnShapeById(this.activeObjectForLeftPanel.Id);
      }
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange();
      this.emitUndoRedoRequest('applyOutlineOnShape');
    },
    applyOutlineOnShapeById(id) {
      console.log('applyOutlineOnShapeById');
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject) { return; }
      let color = '#000000';
      if (selectedObject.type === 'image' && (selectedObject.ImageType === 'SVGImage' || selectedObject.ImageType.indexOf('FDICText') !== -1 || selectedObject.ImageType.indexOf('FDICLogo') !== -1)) {
        selectedObject.SvgProperty.StrokeColor = color || '#000000';
        if (selectedObject.SvgProperty.StrokeWidth == null || selectedObject.SvgProperty.StrokeWidth === 0) {
          selectedObject.SvgProperty.StrokeWidth = 5;
        }
      } else if (selectedObject.type === 'shape') {
        color                      = selectedObject.StrokeColor || '#000000';
        selectedObject.StrokeColor = color;
        if (selectedObject.StrokeWidth == null || selectedObject.StrokeWidth === 0) {
          selectedObject.StrokeWidth = 5;
        }
      }
      if (selectedObject.type == 'shape' && selectedObject.shapeType == 'polygon' && selectedObject.effect && selectedObject.effect !== 'outline') {
        selectedObject.X         -= 1;
        selectedObject.Y         -= 1;
        selectedObject.svgScaleX += 0.005;
        selectedObject.svgScaleY += 0.005;
      }
      this.$set(selectedObject, 'effect', 'outline');

      color = color || '#000000';
      EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CHANGE_SHAPE_STROKE_WIDTH, selectedObject.StrokeWidth || selectedObject.SvgProperty && selectedObject.SvgProperty.StrokeWidth || 5, selectedObject.Id);
      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
      if (selectedObject.type === 'shape') {
        EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CHANGE_SHAPE_STROKE, color, selectedObject.Id);
      } else {
        EventBus.$emit(ApplicationEventTypes.LeftPanelEvents.CHANGE_SVG_STROKE_COLOR_BY_ID, color, selectedObject.Id);
      }

      const elem = document.querySelector(`.canvas-main-html-container [id='${id}']`);
      if (elem) {
        elem.style.filter = 'none'; // Reset the filter to remove any shadow effect
      }
    },
  },
};
